import { Injectable } from '@angular/core';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { IDataSourceApiService, IEntity, INamedEntity } from 'interfaces-lib';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { BullhornSharedServiceProviderService } from 'shared-service-provider-lib';
import { INamedEntityService } from './inamed-entity.service';

@Injectable({ providedIn: 'root' })
export class BullhornNamedEntityService implements INamedEntityService {
  private apiService: IDataSourceApiService;

  namedEntitySubject = new ReplaySubject<INamedEntity>();
  namedEntitySelected$: Observable<INamedEntity> = this.namedEntitySubject.asObservable();

  constructor(private entityControllerService: EntityControllerService, sps: BullhornSharedServiceProviderService) {
    this.apiService = sps.getDataSourceApiService();
    this.entityControllerService.entitySelected$.subscribe((entity) => {
      if (!entity) {
        return;
      }
      this.getNamedEntity(entity).subscribe((namedEntity) => this.namedEntitySubject.next(namedEntity));
    });
  }

  private getNamedEntity(entity: IEntity): Observable<INamedEntity> {
    return this.apiService.get(entity, { fields: this.getFields(entity) }).pipe(map((fullEntity) => this.entityMappers(fullEntity)));
  }

  private getFields(entity: IEntity): string {
    switch (entity.type) {
      case BullhornEntityTypes.Candidate:
        return 'id,firstName,lastName';
      case BullhornEntityTypes.ClientCorporation:
        return 'id,name';
      case BullhornEntityTypes.JobOrder:
        return 'id,title';
      case BullhornEntityTypes.Placement:
        return 'id,candidate(id,firstName,lastName),clientCorporation(name),jobOrder(title)';
      case BullhornEntityTypes.ClientContact:
        return 'id,name';
      case BullhornEntityTypes.Opportunity:
        return 'id,title';
      default:
        return 'id';
    }
  }

  private entityMappers(entityData: any): INamedEntity {
    switch (entityData.type) {
      case BullhornEntityTypes.Candidate:
        return {
          id: entityData.id,
          type: entityData.type,
          name: `${entityData.firstName} ${entityData.lastName}`,
        } as INamedEntity;
      case BullhornEntityTypes.ClientCorporation:
        return {
          id: entityData.id,
          type: entityData.type,
          name: entityData.name,
        } as INamedEntity;
      case BullhornEntityTypes.JobOrder:
        return {
          id: entityData.id,
          type: entityData.type,
          name: `${entityData.title ? entityData.title : ''}`,
        } as INamedEntity;
      case BullhornEntityTypes.Placement:
        return {
          id: entityData.id,
          type: entityData.type,
          name: `${entityData.candidate.firstName} ${entityData.candidate.lastName} - ${entityData.jobOrder.title}`,
        } as INamedEntity;
      case BullhornEntityTypes.ClientContact:
        return {
          id: entityData.id,
          type: entityData.type,
          name: entityData.name,
        } as INamedEntity;
      case BullhornEntityTypes.Opportunity:
        return {
          id: entityData.id,
          type: entityData.type,
          name: `${entityData.title ? entityData.title : ''}`,
        } as INamedEntity;
      default:
        throw new Error(`${entityData.type} not recognised generating named entity.`);
    }
  }
}
